/* -----------------UI--------------- */
import { Button, Dialog } from "react-native-paper";

/* -----------------Helpers & Hooks--------------- */
import { useToast } from "@memorang/ui/src/components/Toast";
import { useKeyboard } from "@react-native-community/hooks";
import { useRef, useState } from "react";

/* -----------------Types--------------- */
import type { IconType } from "@memorang/ui/src";
import DialogWrapper from "@memorang/ui/src/components/DialogWrapper";
import ScrollContainer from "@memorang/ui/src/components/ScrollContainer";
import { useEffect } from "react";
/* -----------------Child components--------------- */
import { FeedbackContent } from "./FeedbackContent";
import { FeedbackHeader } from "./FeedbackHeader";
import type { FeedbackParams, HandleTracking } from "./types";
export { FeedbackHeader } from "./FeedbackHeader";
export { FeedbackReasons } from "./FeedbackReasons";
export { FeedbackForm } from "./FeedbackForm";
export const events = {
	dialogOpened: "mem_dialog_opened",
	dialogClosed: "mem_dialog_closed",
	feedbackSubmitted: "mem_feedback_submitted",
	feedbackPositive: "mem_feedback_positive",
	feedbackNegative: "mem_feedback_negative",
};
interface Props {
	title: string;
	subtitle: string;
	visible: boolean;
	feedbackDialogParams: FeedbackParams;
	subtitleSuffix?: string;
	cta: string;
	cancelCta: string;
	icon?: IconType;
	feedbackReasonList?: {
		title: string;
		description: string;
		icon: IconType;
		value: string;
	}[];
	feedbackLimitError?: string;
	successMessage: string;
	feedbackPlaceholder?: string;
	onDismiss: () => void;
	handlePostFeedback: (feedback: string, email: string) => void;
	sendFeedback: (params: FeedbackParams, feedback: string) => void;
	handleTracking: HandleTracking;
}

const FeedbackDialog = ({
	title,
	visible,
	onDismiss,
	handlePostFeedback,
	feedbackDialogParams,
	subtitle,
	subtitleSuffix,
	feedbackPlaceholder,
	cta,
	handleTracking,
	icon,
	feedbackReasonList,
	sendFeedback,
	cancelCta,
	successMessage,
	feedbackLimitError,
}: Props) => {
	const { showToast } = useToast();
	const { keyboardHeight } = useKeyboard();
	const feedbackContentRef = useRef<{ submit: () => void }>(null);
	const [sendingFeedback, setSendingFeedback] = useState(false);
	const [isValid, setIsValid] = useState(false);
	useEffect(() => {
		if (visible) {
			handleTracking(events.dialogOpened);
		}
	}, [handleTracking, visible]);

	const handleCloseDialog = () => {
		handleTracking(events.dialogClosed);
		onDismiss();
	};

	return (
		<>
			<DialogWrapper
				visible={visible}
				maxWidth="xs"
				showCloseButton={false}
				handleClose={handleCloseDialog}
				bottom={keyboardHeight ? keyboardHeight - 200 : 0}
			>
				<FeedbackHeader icon={icon} title={title} subtitle={subtitle} />
				<ScrollContainer>
					<Dialog.Content
						style={{
							gap: 16,
							padding: 0,
						}}
					>
						<FeedbackContent
							ref={feedbackContentRef}
							sendFeedback={sendFeedback}
							feedbackDialogParams={feedbackDialogParams}
							onDismiss={handleCloseDialog}
							handlePostFeedback={handlePostFeedback}
							subtitle={subtitle}
							subtitleSuffix={subtitleSuffix}
							feedbackPlaceholder={feedbackPlaceholder}
							feedbackLimitError={feedbackLimitError}
							feedbackReasonList={feedbackReasonList}
							showFeedbackConfirmationToast={() =>
								showToast({
									message: successMessage,
								})
							}
							onStateChange={(sending, valid) => {
								setSendingFeedback(sending);
								setIsValid(valid);
							}}
						/>
					</Dialog.Content>
				</ScrollContainer>
				<Dialog.Actions style={{ alignItems: "center", padding: 24 }}>
					<Button onPress={handleCloseDialog}>{cancelCta}</Button>
					<Button
						onPress={() => feedbackContentRef.current?.submit()}
						loading={sendingFeedback}
						disabled={!isValid}
						mode="contained"
						style={{ width: 140 }}
					>
						{cta}
					</Button>
				</Dialog.Actions>
			</DialogWrapper>
		</>
	);
};

export { FeedbackDialog };
